import { appConfig, isUsingMocks, getDqxOverride } from "@/config/app";
import {
  getCachedPipelinePreview,
  invalidatePipelinePreviewCache,
  setCachedPipelinePreview,
} from "@/lib/pipelinePreviewCache";
import { dedupedRequest, invalidateDedupedRequest } from "@/lib/pipelineRequestDedup";
import { sortWorksheetsByRegionNumber } from "@/lib/mappingRegions";
import { trackEvent, trackException } from "@/services/telemetry";
import type {
  TargetSchema,
  UploadConfirmation,
  KnownMapping,
  HistoricalFile,
  SchemaField,
  MappingRow,
  ParsedWorksheet,
  ExtractionPreviewData,
  MappingSelectionRegion,
  DqxValidationResult,
  DqxCellError,
  DqxErrorCategory,
  ClassificationResult,
  FieldRequirement,
  VolumePathConfig,
} from "@/types";
import {
  transformSchemaFields,
  transformProcessResponse,
  transformKnownMappings,
  transformFiles,
  transformUploadResponse,
  transformFieldRequirements,
  transformVolumePathConfig,
} from "@/services/transforms";
import type {
  ApiSchemaField,
  ApiKnownMapping,
  ApiFile,
  ApiFieldRequirement,
  ApiProcessResponse,
  ApiUploadResponse,
  ApiVolumePathConfig,
} from "@/services/transforms";
import {
  mockTargetSchemas,
  mockHistoricalFiles,
  mockKnownMappings,
  simulateUpload,
} from "@/mocks/data";
import { LLOYDS_52_SCHEMA } from "@/mocks/mappingData";
import { generateDynamicMappings } from "@/mocks/mockProcessing";
import { parseFile } from "@/services/fileParser";
import { runMockDqxValidation } from "@/mocks/mockDqx";

// ── Token Provider ─────────────────────────────────────────

type GetTokenFn = () => Promise<string | null>;
let getToken: GetTokenFn = async () => null;

export function registerTokenProvider(fn: GetTokenFn): void {
  getToken = fn;
}

// ── Core Fetch Helpers ─────────────────────────────────────

export async function authHeaders(isMultipart = false): Promise<HeadersInit> {
  const token = await getToken();
  const headers: HeadersInit = {};
  if (token) headers["Authorization"] = `Bearer ${token}`;
  if (!isMultipart) headers["Content-Type"] = "application/json";
  return headers;
}

interface ApiResponseError extends Error {
  statusCode: number;
  code: string;
  /** Populated for some fetches (e.g. contract-rules) so the UI can show the HTTP body on failure. */
  rawResponseText?: string;
}

function parseApiErrorBody(body: unknown, status: number): { code: string; description: string } {
  const fallbackCode = `HTTP_${status}`;
  if (body === null || typeof body !== "object") {
    return { code: fallbackCode, description: String(body ?? "") };
  }
  const record = body as Record<string, unknown>;
  const code =
    (typeof record.code === "string" && record.code) ||
    (typeof record.error === "string" && record.error) ||
    fallbackCode;

  if (typeof record.message === "string" && record.message) {
    return { code, description: record.message };
  }

  const detail = record.detail;
  if (typeof detail === "string" && detail) {
    return { code, description: detail };
  }
  if (Array.isArray(detail) && detail.length > 0) {
    return {
      code,
      description: detail
        .map((item) => {
          if (item && typeof item === "object" && "msg" in item) {
            return String((item as { msg: unknown }).msg);
          }
          return JSON.stringify(item);
        })
        .join("; "),
    };
  }

  return { code, description: "" };
}

/** Human-readable API failure description (error code lives on Error.code). */
function formatApiErrorMessage(body: unknown, status: number): string {
  const { description } = parseApiErrorBody(body, status);
  if (description) return description;
  return `API error: ${status}`;
}

function resolveApiErrorCode(body: unknown, status: number): string {
  return parseApiErrorBody(body, status).code;
}

export interface DqxTriggerResult {
  runId: number;
  dqxJobId: string;
  message?: string | null;
}

export interface DqxRunStatusResult {
  runId: number;
  lifeCycleState: string;
  resultState: string | null;
}

export interface ApplyMappingsResult {
  rows_written: number;
  dqx_triggered?: boolean;
  dqx_run_id?: number | null;
  dqx_job_id?: string | null;
  dqx_trigger_message?: string | null;
  source_row_index_start?: number | null;
}

export type { VolumePathConfig };

export interface TriggerExtractionResult {
  fileId: string;
  batchId: string | null;
  runId: number | null;
  triggered: boolean;
  skipReason: string | null;
  volumePathConfig: VolumePathConfig | null;
}

export interface ExtractionRunStatusResult {
  fileId: string;
  runId: number;
  lifeCycleState: string;
  resultState: string | null;
  stateMessage: string | null;
  failedTaskKey: string | null;
  failedTaskMessage: string | null;
}

export type BatchExtractionStatusCategory =
  | "pending"
  | "running"
  | "succeeded"
  | "failed"
  | "skipped"
  | "cancelled";

export interface BatchExtractionStatusItem {
  fileId: string;
  workflowRunId: number | null;
  triggered: boolean;
  skipReason: string | null;
  triggerError: string | null;
  lifeCycleState: string | null;
  resultState: string | null;
  stateMessage: string | null;
  failedTaskKey: string | null;
  failedTaskMessage: string | null;
  statusCategory: BatchExtractionStatusCategory;
  statusError: string | null;
  rowCount?: number | null;
  tableCount?: number | null;
  globalConfidence?: number | null;
  requiresReview?: boolean | null;
}

export interface BatchExtractionStatus {
  batchId: string;
  totalFiles: number;
  pendingCount: number;
  runningCount: number;
  succeededCount: number;
  failedCount: number;
  skippedCount: number;
  cancelledCount: number;
  items: BatchExtractionStatusItem[];
}

export interface TriggerBatchExtractionResult {
  batchId: string;
  totalFiles: number;
  triggeredCount: number;
  skippedCount: number;
  failedCount: number;
}

export function isAbortError(error: unknown): boolean {
  if (error instanceof DOMException && error.name === "AbortError") {
    return true;
  }
  return error instanceof Error && error.name === "AbortError";
}

export interface CancelBatchExtractionResult {
  batchId: string;
  cancelledCount: number;
  unchangedCount: number;
}

export interface ManualCleanseRevalidateResult {
  audit_entries_written: number;
  rows_rewritten: number;
  cells_applied: number;
  dqx_triggered: boolean;
  dqx_run_id: number | null;
  dqx_job_id: string | null;
  dqx_trigger_message: string | null;
  mapping_run_id: string | null;
}

export interface DqxQuarantineResults {
  dqx_job_id: string;
  // Server-resolved from klyra_files; null when the file row has gone.
  file_id?: string | null;
  schema_id?: string | null;
  quarantine_table: Record<string, unknown>[];
  quarantine_summary_table: Record<string, unknown>[];
}

/** Parsed explicit DQX rules from GET /dqx/contract-rules/{path} */
export interface DqxContractRulesResponse {
  contract_name: string;
  contract_path: string;
  contract_rules: Record<string, unknown>[];
}

/** Resolves a fetch URL: v2 paths use the backend v2 base; other paths use the v1 API base. */
function resolveApiUrl(path: string): string {
  if (path.startsWith("/api/v2/")) {
    return `${appConfig.api.v2BaseUrl}${path.slice("/api/v2".length)}`;
  }
  return `${appConfig.api.baseUrl}${path}`;
}

async function apiFetch<T>(path: string, init?: RequestInit): Promise<T> {
  const headers = await authHeaders();
  const res = await fetch(resolveApiUrl(path), {
    ...init,
    headers: { ...headers, ...init?.headers },
    signal: init?.signal ?? AbortSignal.timeout(appConfig.api.timeout),
  });

  if (!res.ok) {
    const body = await res.json().catch(() => ({}));
    const msg = formatApiErrorMessage(body, res.status);
    const error = new Error(msg);
    (error as ApiResponseError).statusCode = res.status;
    (error as ApiResponseError).code = resolveApiErrorCode(body, res.status);
    trackException(error, {
      endpoint: path,
      statusCode: res.status,
      errorCode: (error as ApiResponseError).code,
      method: init?.method ?? "GET",
    });
    throw error;
  }

  return res.json();
}

async function apiMultipartFetch<T>(
  path: string,
  formData: FormData,
  signal?: AbortSignal
): Promise<T> {
  const headers = await authHeaders(true);
  const res = await fetch(`${appConfig.api.baseUrl}${path}`, {
    method: "POST",
    headers,
    body: formData,
    signal: signal ?? AbortSignal.timeout(appConfig.api.timeout),
  });

  if (!res.ok) {
    const body = await res.json().catch(() => ({}));
    const { code, description } = parseApiErrorBody(body, res.status);
    const msg = description || `Upload failed: ${res.status}`;
    const error = new Error(msg);
    (error as ApiResponseError).statusCode = res.status;
    (error as ApiResponseError).code = code;
    trackException(error, {
      endpoint: path,
      statusCode: res.status,
      errorCode: code,
      method: "POST_MULTIPART",
    });
    throw error;
  }

  return res.json();
}

// ── Feature flag ────────────────────────────────────────────
// Evaluated per-call so ?mocks=true URL override works at runtime
const shouldUseMocks = isUsingMocks;

// ── API Methods ────────────────────────────────────────────

export interface BackendHealthResponse {
  status: string;
  service: string;
  version: string;
  uptime_seconds: number;
  env?: string;
}

/** Resolve GET /health URL from the configured API base (health is not under /api/v1). */
export function resolveBackendHealthUrl(): string {
  const base = appConfig.api.baseUrl.replace(/\/$/, "");
  if (base.startsWith("http://") || base.startsWith("https://")) {
    const url = new URL(base);
    url.pathname = "/health";
    url.search = "";
    url.hash = "";
    return url.toString();
  }
  return "/health";
}

export async function fetchBackendHealth(): Promise<BackendHealthResponse> {
  if (shouldUseMocks()) {
    return {
      status: "ok",
      service: "kodex-api",
      version: "0.1.0",
      uptime_seconds: 0,
      env: "development",
    };
  }

  const url = resolveBackendHealthUrl();
  const headers = await authHeaders();
  const res = await fetch(url, {
    headers,
    signal: AbortSignal.timeout(appConfig.api.timeout),
  });

  if (!res.ok) {
    const raw = await res.text();
    throw new Error(raw.trim() || `Health check failed: ${res.status}`);
  }

  return res.json() as Promise<BackendHealthResponse>;
}

export async function fetchTargetSchemas(): Promise<TargetSchema[]> {
  if (shouldUseMocks()) {
    return new Promise((resolve) => setTimeout(() => resolve(mockTargetSchemas), 200));
  }
  return dedupedRequest(
    "target-schemas",
    async () => {
      const data = await apiFetch<{ id: string; name: string }[]>("/schemas");
      return data.map((s) => ({ id: s.id, name: s.name, version: "" }));
    },
    5 * 60 * 1000
  );
}

export async function fetchSchemaFields(
  schemaId?: string,
  fieldCategory?: string,
  reportType?: string
): Promise<SchemaField[]> {
  if (shouldUseMocks()) {
    return new Promise((resolve) => setTimeout(() => resolve(LLOYDS_52_SCHEMA), 200));
  }
  const params = new URLSearchParams();
  if (schemaId) params.set("schema_id", schemaId);
  if (fieldCategory) params.set("field_category", fieldCategory);
  // When provided, the backend returns is_mandatory scoped to this report_type
  // so the mapping UI only marks fields mandatory for the selected contract.
  if (reportType) params.set("report_type", reportType);
  const qs = params.toString();
  const data = await apiFetch<ApiSchemaField[]>(`/schema-fields${qs ? `?${qs}` : ""}`);
  trackEvent("api_schema_fields_fetched", {
    count: data.length,
    schemaId: schemaId ?? "all",
    fieldCategory: fieldCategory ?? "all",
  });
  return transformSchemaFields(data);
}

/** @deprecated Use fetchSchemaFields */
export const fetchTargetColumns = fetchSchemaFields;

export async function uploadFile(
  file: File,
  schemaId: string,
  signal?: AbortSignal,
  providerId?: string | null
): Promise<UploadConfirmation> {
  if (shouldUseMocks()) {
    const result = await simulateUpload();
    trackEvent("FileUpload.Succeeded", {
      documentId: result.fileId,
      fileName: file.name,
      fileSize: file.size,
      schemaId,
      mock: true,
    });
    return {
      documentId: result.fileId,
      fileName: file.name,
      uploadedBy: "Mock User",
      uploadedAt: new Date().toISOString(),
      targetSchema: schemaId,
      status: "success",
      fileSize: file.size,
      mimeType: file.type || undefined,
    };
  }

  const formData = new FormData();
  formData.append("file", file);
  formData.append("schemaId", schemaId);
  if (providerId) formData.append("providerId", providerId);

  const raw = await apiMultipartFetch<ApiUploadResponse>("/files/upload", formData, signal);
  return transformUploadResponse(raw);
}

/** Upload a file for schema inference (no schema association required). Returns the file_id. */
export async function uploadSchemaBuilderFile(file: File): Promise<string> {
  if (shouldUseMocks()) {
    return new Promise((resolve) =>
      setTimeout(() => resolve(`mock-file-${crypto.randomUUID()}`), 300)
    );
  }
  const formData = new FormData();
  formData.append("file", file);
  formData.append("schemaId", "schema-builder");
  const raw = await apiMultipartFetch<ApiUploadResponse>("/files/upload", formData);
  return raw.documentId;
}

function parseContentDispositionFilename(header: string | null): string | null {
  if (!header) return null;
  const utf8Match = header.match(/filename\*=UTF-8''([^;]+)/i);
  if (utf8Match?.[1]) {
    try {
      return decodeURIComponent(utf8Match[1]);
    } catch {
      // fall through to quoted filename
    }
  }
  const quoted = header.match(/filename="([^"]+)"/i);
  if (quoted?.[1]) return quoted[1];
  const unquoted = header.match(/filename=([^;]+)/i);
  if (unquoted?.[1]) return unquoted[1].trim();
  return null;
}

/** Download the original uploaded file bytes for batch mapping resume. */
export async function downloadUploadedFile(
  fileId: string,
  fallbackFileName: string
): Promise<File> {
  if (shouldUseMocks()) {
    return new File(["mock"], fallbackFileName, { type: "application/octet-stream" });
  }

  const headers = await authHeaders();
  const res = await fetch(`${appConfig.api.baseUrl}/files/${encodeURIComponent(fileId)}/download`, {
    headers,
    signal: AbortSignal.timeout(appConfig.api.timeout),
  });

  if (!res.ok) {
    const body = await res.json().catch(() => ({}));
    const msg = formatApiErrorMessage(body, res.status);
    const error = new Error(msg);
    (error as ApiResponseError).statusCode = res.status;
    (error as ApiResponseError).code = resolveApiErrorCode(body, res.status);
    trackException(error, {
      endpoint: `/files/${fileId}/download`,
      statusCode: res.status,
      errorCode: (error as ApiResponseError).code,
      method: "GET",
    });
    throw error;
  }

  const bytes = await res.arrayBuffer();
  const fileName =
    parseContentDispositionFilename(res.headers.get("Content-Disposition")) ?? fallbackFileName;
  const mimeType = res.headers.get("Content-Type") ?? "application/octet-stream";
  return new File([bytes], fileName, { type: mimeType });
}

const XLSX_MIME = "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet";

function triggerDownload(blob: Blob, filename: string): void {
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = filename;
  link.style.display = "none";
  document.body.appendChild(link);
  link.click();
  setTimeout(() => {
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  }, 100);
}

/**
 * Download silver mapped data as Excel (kodex_mapped_data) and trigger a browser save.
 * Filename comes from Content-Disposition when present.
 */
export async function downloadMappedFileXlsx(
  fileId: string,
  fallbackFileName: string
): Promise<void> {
  if (shouldUseMocks()) {
    triggerDownload(new Blob(["mock"], { type: XLSX_MIME }), fallbackFileName);
    return;
  }

  const headers = await authHeaders();
  const res = await fetch(
    `${appConfig.api.baseUrl}/files/${encodeURIComponent(fileId)}/mapped.xlsx`,
    {
      headers,
      signal: AbortSignal.timeout(appConfig.api.timeout),
    }
  );

  if (!res.ok) {
    const body = await res.json().catch(() => ({}));
    const msg = formatApiErrorMessage(body, res.status);
    const error = new Error(msg);
    (error as ApiResponseError).statusCode = res.status;
    (error as ApiResponseError).code = resolveApiErrorCode(body, res.status);
    trackException(error, {
      endpoint: `/files/${fileId}/mapped.xlsx`,
      statusCode: res.status,
      errorCode: (error as ApiResponseError).code,
      method: "GET",
    });
    throw error;
  }

  const bytes = await res.arrayBuffer();
  const fileName =
    parseContentDispositionFilename(res.headers.get("Content-Disposition")) ?? fallbackFileName;
  const mimeType = res.headers.get("Content-Type") ?? XLSX_MIME;
  triggerDownload(new Blob([bytes], { type: mimeType }), fileName);
}

/** Permanently delete an uploaded file: blob, catalogue row, and extraction artifacts. */
export async function deleteUploadedFile(fileId: string): Promise<void> {
  if (shouldUseMocks()) return;
  await apiFetch<{ file_id: string; deleted: boolean }>(`/files/${encodeURIComponent(fileId)}`, {
    method: "DELETE",
  });
}

/** Release backend artifacts a completed stage no longer needs (blob, manifest, extraction JSONs). */
export async function cleanupFileArtifacts(
  fileId: string,
  completedStage: "structure_confirmed" | "dq_complete"
): Promise<void> {
  if (shouldUseMocks()) return;
  await apiFetch<{ file_id: string; cleaned: boolean }>(
    `/files/${encodeURIComponent(fileId)}/cleanup`,
    { method: "POST", body: JSON.stringify({ completed_stage: completedStage }) }
  );
}

/** Permanently delete several uploaded files and all their derived data in one call. */
export async function deleteUploadedFiles(fileIds: string[]): Promise<void> {
  if (fileIds.length === 0) return;
  if (shouldUseMocks()) return;
  await apiFetch<{ results: { file_id: string; deleted: boolean }[] }>("/files/delete-batch", {
    method: "POST",
    body: JSON.stringify({ file_ids: fileIds }),
  });
}

export async function triggerExtractionWorkflow(fileId: string): Promise<TriggerExtractionResult> {
  if (shouldUseMocks()) {
    return {
      fileId,
      batchId: `single-${fileId}`,
      runId: Date.now(),
      triggered: true,
      skipReason: null,
      volumePathConfig: null,
    };
  }

  const raw = await apiFetch<{
    file_id: string;
    batch_id: string | null;
    workflow_run_id: number | null;
    triggered: boolean;
    skip_reason: string | null;
    volume_path_config?: ApiVolumePathConfig;
  }>(`/files/${encodeURIComponent(fileId)}/extract`, {
    method: "POST",
    body: JSON.stringify({}),
  });

  return {
    fileId: raw.file_id,
    batchId: raw.batch_id ?? null,
    runId: raw.workflow_run_id,
    triggered: raw.triggered,
    skipReason: raw.skip_reason,
    volumePathConfig: transformVolumePathConfig(raw.volume_path_config),
  };
}

export async function fetchExtractionWorkflowStatus(
  fileId: string,
  runId: number
): Promise<ExtractionRunStatusResult> {
  if (shouldUseMocks()) {
    return {
      fileId,
      runId,
      lifeCycleState: "TERMINATED",
      resultState: "SUCCESS",
      stateMessage: "Completed",
      failedTaskKey: null,
      failedTaskMessage: null,
    };
  }

  const raw = await apiFetch<{
    file_id: string;
    run_id: number;
    life_cycle_state: string;
    result_state: string | null;
    state_message: string | null;
    failed_task_key: string | null;
    failed_task_message: string | null;
  }>(`/files/${encodeURIComponent(fileId)}/extract/status/${runId}`);

  return {
    fileId: raw.file_id,
    runId: raw.run_id,
    lifeCycleState: raw.life_cycle_state,
    resultState: raw.result_state,
    stateMessage: raw.state_message,
    failedTaskKey: raw.failed_task_key,
    failedTaskMessage: raw.failed_task_message,
  };
}

export async function uploadFiles(
  files: File[],
  schemaId: string,
  options?: { concurrency?: number; signal?: AbortSignal }
): Promise<UploadConfirmation[]> {
  const concurrency = Math.max(1, options?.concurrency ?? 3);
  const signal = options?.signal;
  const results: UploadConfirmation[] = new Array(files.length);
  let nextIndex = 0;

  async function worker(): Promise<void> {
    while (nextIndex < files.length) {
      if (signal?.aborted) {
        throw new DOMException("Upload aborted", "AbortError");
      }
      const index = nextIndex;
      nextIndex += 1;
      const file = files[index];
      if (!file) {
        throw new Error(`Missing file at upload index ${index}`);
      }
      results[index] = await uploadFile(file, schemaId, signal);
    }
  }

  const workers = Array.from({ length: Math.min(concurrency, files.length) }, () => worker());
  await Promise.all(workers);
  return results;
}

// Mock batches keep the triggered file ids so status polls can report per-file
// progress; the real endpoint derives this from the ingestion queue.
const mockBatchRuns = new Map<string, { fileIds: string[]; triggeredAt: number }>();

function mockBatchItemCategory(elapsedMs: number, index: number): BatchExtractionStatusCategory {
  const fileElapsed = elapsedMs - index * 400;
  if (fileElapsed < 1000) return "pending";
  if (fileElapsed < 2500) return "running";
  return "succeeded";
}

export async function triggerBatchExtraction(
  fileIds: string[],
  options?: { batchId?: string }
): Promise<TriggerBatchExtractionResult> {
  const requestedBatchId = options?.batchId?.trim() || undefined;
  if (shouldUseMocks()) {
    const batchId = requestedBatchId ?? `batch-mock-${Date.now()}`;
    const existing = mockBatchRuns.get(batchId);
    if (existing) {
      const mergedIds = [...existing.fileIds];
      for (const fileId of fileIds) {
        if (!mergedIds.includes(fileId)) mergedIds.push(fileId);
      }
      mockBatchRuns.set(batchId, { ...existing, fileIds: mergedIds });
    } else {
      mockBatchRuns.set(batchId, { fileIds: [...fileIds], triggeredAt: Date.now() });
    }
    const run = mockBatchRuns.get(batchId)!;
    return {
      batchId,
      totalFiles: run.fileIds.length,
      triggeredCount: fileIds.length,
      skippedCount: 0,
      failedCount: 0,
    };
  }

  const raw = await apiFetch<{
    batch_id: string;
    total_files: number;
    triggered_count: number;
    skipped_count: number;
    failed_count: number;
  }>("/files/extract/batch", {
    method: "POST",
    body: JSON.stringify({
      file_ids: fileIds,
      ...(requestedBatchId ? { batch_id: requestedBatchId } : {}),
    }),
  });

  return {
    batchId: raw.batch_id,
    totalFiles: raw.total_files,
    triggeredCount: raw.triggered_count,
    skippedCount: raw.skipped_count,
    failedCount: raw.failed_count,
  };
}

export async function fetchBatchExtractionStatus(
  batchId: string,
  signal?: AbortSignal
): Promise<BatchExtractionStatus> {
  if (shouldUseMocks()) {
    const run = mockBatchRuns.get(batchId);
    const elapsed = run ? Date.now() - run.triggeredAt : 0;
    const items: BatchExtractionStatusItem[] = (run?.fileIds ?? []).map((fileId, index) => {
      const category = mockBatchItemCategory(elapsed, index);
      return {
        fileId,
        workflowRunId: null,
        triggered: true,
        skipReason: null,
        triggerError: null,
        lifeCycleState: category === "succeeded" ? "TERMINATED" : "RUNNING",
        resultState: category === "succeeded" ? "SUCCESS" : null,
        stateMessage: null,
        failedTaskKey: null,
        failedTaskMessage: null,
        statusCategory: category,
        statusError: null,
      };
    });
    const countFor = (category: BatchExtractionStatusCategory) =>
      items.filter((item) => item.statusCategory === category).length;
    return {
      batchId,
      totalFiles: items.length,
      pendingCount: countFor("pending"),
      runningCount: countFor("running"),
      succeededCount: countFor("succeeded"),
      failedCount: 0,
      skippedCount: 0,
      cancelledCount: 0,
      items,
    };
  }

  return dedupedRequest(`batch-status:${batchId}`, () =>
    fetchBatchExtractionStatusRaw(batchId, signal)
  );
}

async function fetchBatchExtractionStatusRaw(
  batchId: string,
  signal?: AbortSignal
): Promise<BatchExtractionStatus> {
  const raw = await apiFetch<{
    batch_id: string;
    total_files: number;
    pending_count: number;
    running_count: number;
    succeeded_count: number;
    failed_count: number;
    skipped_count: number;
    cancelled_count: number;
    items: {
      file_id: string;
      workflow_run_id: number | null;
      triggered: boolean;
      skip_reason: string | null;
      trigger_error: string | null;
      life_cycle_state: string | null;
      result_state: string | null;
      state_message: string | null;
      failed_task_key: string | null;
      failed_task_message: string | null;
      status_category: BatchExtractionStatusCategory;
      status_error: string | null;
      row_count?: number | null;
      table_count?: number | null;
      global_confidence?: number | null;
      requires_review?: boolean | null;
    }[];
  }>(`/files/extract/batch/${encodeURIComponent(batchId)}/status`, { signal });

  return {
    batchId: raw.batch_id,
    totalFiles: raw.total_files,
    pendingCount: raw.pending_count,
    runningCount: raw.running_count,
    succeededCount: raw.succeeded_count,
    failedCount: raw.failed_count,
    skippedCount: raw.skipped_count,
    cancelledCount: raw.cancelled_count ?? 0,
    items: raw.items.map((item) => ({
      fileId: item.file_id,
      workflowRunId: item.workflow_run_id,
      triggered: item.triggered,
      skipReason: item.skip_reason,
      triggerError: item.trigger_error,
      lifeCycleState: item.life_cycle_state,
      resultState: item.result_state,
      stateMessage: item.state_message,
      failedTaskKey: item.failed_task_key,
      failedTaskMessage: item.failed_task_message,
      statusCategory: item.status_category,
      statusError: item.status_error,
      rowCount: item.row_count ?? null,
      tableCount: item.table_count ?? null,
      globalConfidence: item.global_confidence ?? null,
      requiresReview: item.requires_review ?? null,
    })),
  };
}

export async function fetchFileExtractionMetrics(
  fileId: string,
  signal?: AbortSignal
): Promise<{
  rowCount: number;
  tableCount: number;
  globalConfidence: number | null;
  requiresReview: boolean;
  reviewThreshold: number | null;
}> {
  if (shouldUseMocks()) {
    return {
      rowCount: 0,
      tableCount: 0,
      globalConfidence: null,
      requiresReview: false,
      reviewThreshold: null,
    };
  }

  const raw = await apiFetch<{
    file_id: string;
    row_count: number;
    table_count: number;
    global_confidence?: number | null;
    requires_review?: boolean;
    review_threshold?: number | null;
  }>(`/files/${encodeURIComponent(fileId)}/metrics`, { signal });

  return {
    rowCount: raw.row_count,
    tableCount: raw.table_count,
    globalConfidence: typeof raw.global_confidence === "number" ? raw.global_confidence : null,
    requiresReview: !!raw.requires_review,
    reviewThreshold: typeof raw.review_threshold === "number" ? raw.review_threshold : null,
  };
}

export function invalidateFilePreviewCache(fileId: string): void {
  invalidatePipelinePreviewCache(fileId);
  invalidateDedupedRequest(`preview:${fileId}`);
}

export async function cancelBatchExtraction(batchId: string): Promise<CancelBatchExtractionResult> {
  if (shouldUseMocks()) {
    return {
      batchId,
      cancelledCount: 0,
      unchangedCount: 0,
    };
  }

  const raw = await apiFetch<{
    batch_id: string;
    cancelled_count: number;
    unchanged_count: number;
  }>(`/files/extract/batch/${encodeURIComponent(batchId)}/cancel`, {
    method: "POST",
    body: JSON.stringify({}),
  });

  return {
    batchId: raw.batch_id,
    cancelledCount: raw.cancelled_count,
    unchangedCount: raw.unchanged_count,
  };
}

export function formatExtractionRunStatusLabel(lifeCycleState: string): string {
  const state = lifeCycleState.toUpperCase();
  if (state === "PENDING" || state === "QUEUED" || state === "BLOCKED") {
    return "Queued on Databricks…";
  }
  if (state === "RUNNING" || state === "TERMINATING") {
    return "Running structural analysis and extraction…";
  }
  if (state === "WAITING_FOR_RETRY") {
    return "Waiting to retry…";
  }
  return `Workflow status: ${lifeCycleState}`;
}

/** Legacy upload preview: parse file client-side (mocks) or multipart POST /files/preview. */
export async function previewUploadFile(file: File): Promise<ParsedWorksheet[]> {
  if (shouldUseMocks()) {
    return parseFile(file);
  }

  const formData = new FormData();
  formData.append("file", file);

  const raw = await apiMultipartFetch<{
    worksheets: {
      name: string;
      headers: string[];
      rows: Record<string, string>[];
      total_rows: number;
      is_metadata: boolean;
    }[];
  }>("/files/preview", formData);

  return raw.worksheets.map((ws) => ({
    name: ws.name,
    headers: ws.headers,
    rows: ws.rows,
    totalRows: ws.total_rows,
    isMetadata: ws.is_metadata,
  }));
}

const MOCK_MAPPING_SELECTION_KEY = "klyra_mock_mapping_selection";

function mockMappingSelectionStore(): Record<string, MappingSelectionRegion[]> {
  try {
    const raw = sessionStorage.getItem(MOCK_MAPPING_SELECTION_KEY);
    if (!raw) return {};
    const parsed = JSON.parse(raw) as Record<string, MappingSelectionRegion[]>;
    return parsed && typeof parsed === "object" ? parsed : {};
  } catch {
    return {};
  }
}

function saveMockMappingSelection(fileId: string, regions: MappingSelectionRegion[]): void {
  try {
    const store = mockMappingSelectionStore();
    store[fileId] = regions;
    sessionStorage.setItem(MOCK_MAPPING_SELECTION_KEY, JSON.stringify(store));
  } catch {
    // Ignore quota errors in mock mode.
  }
}

function mockExtractionPreviewWorksheets(): ParsedWorksheet[] {
  return [
    {
      name: "Mapping - region_group_001",
      headers: ["Policy", "Premium"],
      rows: [{ Policy: "P-1", Premium: "100" }],
      totalRows: 120,
      previewCategory: "mapping",
      regionId: "region_group_001",
      sheetNames: ["Sheet1", "Q2"],
      previewKey: "mapping:region_group_001",
    },
    {
      name: "Mapping - region_group_002",
      headers: ["Claim", "Amount"],
      rows: [{ Claim: "C-1", Amount: "50" }],
      totalRows: 85,
      previewCategory: "mapping",
      regionId: "region_group_002",
      sheetNames: ["Claims"],
      previewKey: "mapping:region_group_002",
    },
    {
      name: "Non-Mapping - region_meta (metadata 1)",
      headers: ["Field", "Value"],
      rows: [{ Field: "Broker", Value: "ACME" }],
      totalRows: 4,
      isMetadata: true,
      previewCategory: "non_mapping",
      sourceRegionId: "region_meta",
      sectionIndex: 0,
      previewKey: "non_mapping:region_meta:0",
    },
    {
      name: "Ambiguity - region_amb (sheet 1)",
      headers: ["ColA", "ColB"],
      rows: [{ ColA: "1", ColB: "2" }],
      totalRows: 12,
      isMetadata: true,
      previewCategory: "ambiguity",
      sourceRegionId: "region_amb",
      sectionIndex: 0,
      previewKey: "ambiguity:region_amb:0",
    },
  ];
}

/** Complex ingestion preview after extraction workflow completes. */
export async function previewExtraction(fileId: string): Promise<ExtractionPreviewData> {
  const cached = getCachedPipelinePreview(fileId);
  if (cached) {
    return cached;
  }

  if (shouldUseMocks()) {
    const preview = {
      worksheets: mockExtractionPreviewWorksheets(),
      globalConfidence: 0.88,
      reviewThreshold: 0.75,
      requiresReview: false,
      reviewReason: null,
      processingDurationMs: 1250,
      structuralDurationMs: 800,
      extractionDurationMs: 450,
      ingestionSkipped: false,
      executionMode: "inline" as const,
    };
    setCachedPipelinePreview(fileId, preview);
    return preview;
  }

  return dedupedRequest(`preview:${fileId}`, async () => {
    const preview = await fetchPreviewExtractionRaw(fileId);
    setCachedPipelinePreview(fileId, preview);
    return preview;
  });
}

async function fetchPreviewExtractionRaw(fileId: string): Promise<ExtractionPreviewData> {
  const raw = await apiFetch<{
    file_id: string;
    mapping_regions: {
      region_id: string;
      source_region_ids: string[];
      sheet_names?: string[];
      headers: string[];
      rows: Record<string, unknown>[];
      total_rows: number;
    }[];
    non_mapping_regions: {
      source_region_id: string;
      sections: {
        section_type: string;
        headers_resolved: string[];
        records: Record<string, unknown>[];
      }[];
      total_rows: number;
    }[];
    uncertain_regions: {
      source_region_id: string;
      sections: {
        section_type: string;
        headers_resolved: string[];
        records: Record<string, unknown>[];
      }[];
      total_rows: number;
    }[];
    global_confidence: number | null;
    review_threshold: number;
    requires_review: boolean;
    review_reason: string | null;
    processing_duration_ms: number | null;
    structural_duration_ms: number | null;
    extraction_duration_ms: number | null;
    ingestion_skipped: boolean;
    execution_mode: string | null;
  }>("/files/preview", {
    method: "POST",
    body: JSON.stringify({ file_id: fileId }),
  });

  const mappingWorksheets: ParsedWorksheet[] = raw.mapping_regions.map((region) => ({
    name: `Mapping - ${region.region_id}`,
    headers: region.headers,
    rows: region.rows,
    totalRows: region.total_rows,
    isMetadata: false,
    previewCategory: "mapping",
    regionId: region.region_id,
    sourceRegionIds: region.source_region_ids ?? [],
    sheetNames: Array.isArray(region.sheet_names)
      ? region.sheet_names.filter(
          (name): name is string => typeof name === "string" && name.trim() !== ""
        )
      : undefined,
    previewKey: `mapping:${region.region_id}`,
  }));
  const nonMappingWorksheets: ParsedWorksheet[] = raw.non_mapping_regions.flatMap((region) =>
    region.sections.map((section, index) => {
      const sectionHeaders =
        section.headers_resolved.length > 0
          ? section.headers_resolved
          : Array.from(
              new Set(
                section.records.flatMap((record) =>
                  record && typeof record === "object" ? Object.keys(record) : []
                )
              )
            );
      return {
        name: `Non-Mapping - ${region.source_region_id} (${section.section_type} ${index + 1})`,
        headers: sectionHeaders,
        rows: section.records,
        totalRows: section.records.length,
        isMetadata: true,
        previewCategory: "non_mapping",
        sourceRegionId: region.source_region_id,
        sectionIndex: index,
        sectionType: section.section_type,
        previewKey: `non_mapping:${region.source_region_id}:${index}`,
      };
    })
  );
  const uncertainRegions = Array.isArray(raw.uncertain_regions) ? raw.uncertain_regions : [];
  const ambiguityWorksheets: ParsedWorksheet[] = uncertainRegions.flatMap((region) =>
    region.sections.map((section, index) => {
      const sectionHeaders =
        section.headers_resolved.length > 0
          ? section.headers_resolved
          : Array.from(
              new Set(
                section.records.flatMap((record) =>
                  record && typeof record === "object" ? Object.keys(record) : []
                )
              )
            );
      return {
        name: `Ambiguity - ${region.source_region_id} (${section.section_type} ${index + 1})`,
        headers: sectionHeaders,
        rows: section.records,
        totalRows: section.records.length,
        isMetadata: true,
        previewCategory: "ambiguity",
        sourceRegionId: region.source_region_id,
        sectionIndex: index,
        sectionType: section.section_type,
        previewKey: `ambiguity:${region.source_region_id}:${index}`,
      };
    })
  );

  const coerceDurationMs = (value: unknown): number | null =>
    typeof value === "number" && Number.isFinite(value) && value >= 0 ? Math.round(value) : null;

  return {
    worksheets: [
      ...sortWorksheetsByRegionNumber(mappingWorksheets),
      ...nonMappingWorksheets,
      ...ambiguityWorksheets,
    ],
    globalConfidence: typeof raw.global_confidence === "number" ? raw.global_confidence : null,
    reviewThreshold: typeof raw.review_threshold === "number" ? raw.review_threshold : 0.75,
    requiresReview: !!raw.requires_review,
    reviewReason: typeof raw.review_reason === "string" ? raw.review_reason : null,
    processingDurationMs: coerceDurationMs(raw.processing_duration_ms),
    structuralDurationMs: coerceDurationMs(raw.structural_duration_ms),
    extractionDurationMs: coerceDurationMs(raw.extraction_duration_ms),
    ingestionSkipped: !!raw.ingestion_skipped,
    executionMode:
      typeof raw.execution_mode === "string" && raw.execution_mode.trim()
        ? raw.execution_mode.trim().toLowerCase()
        : null,
  };
}

/** Persist user table mapping selection from the confirm-structure step. */
function serializeMappingSelectionRegion(region: MappingSelectionRegion) {
  return {
    preview_key: region.previewKey,
    included: region.included,
    promoted: region.promoted ?? false,
    region_id: region.regionId ?? null,
    source_region_id: region.sourceRegionId ?? null,
    section_index: region.sectionIndex ?? null,
    preview_category: region.previewCategory ?? null,
  };
}

export async function saveMappingSelection(
  fileId: string,
  regions: MappingSelectionRegion[]
): Promise<void> {
  if (shouldUseMocks()) {
    saveMockMappingSelection(fileId, regions);
    return;
  }
  await apiFetch<{ file_id: string; saved: boolean }>("/files/mapping-selection", {
    method: "POST",
    body: JSON.stringify({
      file_id: fileId,
      regions: regions.map(serializeMappingSelectionRegion),
    }),
  });
}

/** Load a previously saved mapping-selection for confirm-structure re-open. */
export async function loadMappingSelection(fileId: string): Promise<MappingSelectionRegion[]> {
  if (shouldUseMocks()) {
    return mockMappingSelectionStore()[fileId] ?? [];
  }
  const raw = await apiFetch<{
    file_id: string;
    regions: {
      preview_key: string;
      included: boolean;
      promoted?: boolean;
      region_id?: string | null;
      source_region_id?: string | null;
      section_index?: number | null;
      preview_category?: MappingSelectionRegion["previewCategory"];
    }[];
  }>(`/files/mapping-selection/${encodeURIComponent(fileId)}`);
  return (raw.regions ?? []).map((region) => ({
    previewKey: region.preview_key,
    included: region.included,
    promoted: region.promoted,
    regionId: region.region_id ?? null,
    sourceRegionId: region.source_region_id ?? null,
    sectionIndex: region.section_index ?? null,
    previewCategory: region.preview_category,
  }));
}

interface ApiMappingSnapshotRow {
  source_field: string;
  target_field: string;
  target_field_id?: string | null;
  schema_id: string;
  region_id?: string | null;
  report_type?: string | null;
}

/** Persist the approved mapping set server-side so "Mapped" survives machines. */
export async function saveMappingSnapshot(fileId: string, mappings: KnownMapping[]): Promise<void> {
  if (shouldUseMocks()) return;
  await apiFetch<{ file_id: string; saved: boolean }>("/files/mapping-snapshot", {
    method: "POST",
    body: JSON.stringify({
      file_id: fileId,
      mappings: mappings.map(
        (m): ApiMappingSnapshotRow => ({
          source_field: m.sourceField,
          target_field: m.targetField,
          target_field_id: m.targetFieldId ?? null,
          schema_id: m.schemaId,
          region_id: m.regionId ?? null,
          report_type: m.reportType ?? null,
        })
      ),
    }),
  });
}

/** Null when no snapshot exists (or it cannot be fetched) — caller falls back to local state. */
export async function fetchMappingSnapshot(fileId: string): Promise<KnownMapping[] | null> {
  if (shouldUseMocks()) return null;
  try {
    const raw = await apiFetch<{ file_id: string; mappings: ApiMappingSnapshotRow[] }>(
      `/files/mapping-snapshot/${encodeURIComponent(fileId)}`
    );
    const rows = raw.mappings ?? [];
    if (rows.length === 0) return null;
    return rows.map(
      (m, index): KnownMapping => ({
        mappingId: `${fileId}:${m.target_field_id ?? m.target_field ?? index}`,
        sourceField: m.source_field,
        targetField: m.target_field,
        targetFieldId: m.target_field_id ?? undefined,
        schemaId: m.schema_id,
        regionId: m.region_id ?? undefined,
        reportType: m.report_type ?? null,
      })
    );
  } catch {
    return null;
  }
}

export interface BoardStateFile {
  fileId: string;
  fileName: string;
  schemaId: string;
  /** Human schema name from board-state; prefer over client schema cache. */
  schemaName?: string | null;
  reportType: string | null;
  batchId: string | null;
  extractionCategory: BatchExtractionStatusCategory;
  structureConfirmed: boolean;
  mapped: boolean;
  rowCount: number | null;
  tableCount: number | null;
  providerId: string | null;
  uploadedBy: string | null;
  createdAt: string | null;
  dqx: {
    runId: number;
    jobId: string | null;
    complete: boolean;
    rowsPassing: number | null;
    rowsWithIssues: number | null;
    totalRowsChecked: number | null;
    regionId?: string | null;
    runs?: {
      runId: number;
      jobId: string | null;
      regionId?: string | null;
      complete: boolean;
      rowsPassing: number | null;
      rowsWithIssues: number | null;
      totalRowsChecked: number | null;
    }[];
  } | null;
}

export interface BoardStateFilters {
  providerId?: string | null;
  uploadedBy?: string | null;
}

/**
 * Drop a published file's queue rows so its card leaves the board (and stays
 * gone across restores). Gold output, mapped data, and history are kept.
 */
export async function dismissPublishedFile(fileId: string): Promise<void> {
  if (shouldUseMocks()) return;
  await apiFetch<{ file_id: string; dismissed: boolean }>(
    `/files/${encodeURIComponent(fileId)}/dismiss`,
    { method: "POST" }
  );
}

/** Server-derived pipeline board state — used to rebuild the board on a new machine/browser. */
export async function fetchBoardState(filters: BoardStateFilters = {}): Promise<BoardStateFile[]> {
  if (shouldUseMocks()) return [];
  const params = new URLSearchParams();
  const providerId = filters.providerId?.trim();
  const uploadedBy = filters.uploadedBy?.trim();
  if (providerId) params.set("provider_id", providerId);
  if (uploadedBy) params.set("uploaded_by", uploadedBy);
  const qs = params.toString();
  const raw = await apiFetch<{
    files: {
      file_id: string;
      file_name: string;
      schema_id: string;
      schema_name?: string | null;
      report_type?: string | null;
      batch_id?: string | null;
      extraction_category: BatchExtractionStatusCategory;
      structure_confirmed: boolean;
      mapped: boolean;
      row_count?: number | null;
      table_count?: number | null;
      provider_id?: string | null;
      uploaded_by?: string | null;
      created_at?: string | null;
      dqx?: {
        run_id: number;
        job_id?: string | null;
        complete: boolean;
        rows_passing?: number | null;
        rows_with_issues?: number | null;
        total_rows_checked?: number | null;
        region_id?: string | null;
        runs?: {
          run_id: number;
          job_id?: string | null;
          region_id?: string | null;
          complete: boolean;
          rows_passing?: number | null;
          rows_with_issues?: number | null;
          total_rows_checked?: number | null;
        }[];
      } | null;
    }[];
  }>(`/files/board-state${qs ? `?${qs}` : ""}`);
  return (raw.files ?? []).map((f) => ({
    fileId: f.file_id,
    fileName: f.file_name,
    schemaId: f.schema_id,
    schemaName: f.schema_name?.trim() || null,
    reportType: f.report_type ?? null,
    batchId: f.batch_id ?? null,
    extractionCategory: f.extraction_category,
    structureConfirmed: f.structure_confirmed,
    mapped: f.mapped,
    rowCount: f.row_count ?? null,
    tableCount: f.table_count ?? null,
    providerId: f.provider_id ?? null,
    uploadedBy: f.uploaded_by ?? null,
    createdAt: f.created_at ?? null,
    dqx: f.dqx
      ? {
          runId: f.dqx.run_id,
          jobId: f.dqx.job_id ?? null,
          complete: f.dqx.complete,
          rowsPassing: f.dqx.rows_passing ?? null,
          rowsWithIssues: f.dqx.rows_with_issues ?? null,
          totalRowsChecked: f.dqx.total_rows_checked ?? null,
          regionId: f.dqx.region_id ?? null,
          runs: (f.dqx.runs ?? []).map((run) => ({
            runId: run.run_id,
            jobId: run.job_id ?? null,
            regionId: run.region_id ?? null,
            complete: run.complete,
            rowsPassing: run.rows_passing ?? null,
            rowsWithIssues: run.rows_with_issues ?? null,
            totalRowsChecked: run.total_rows_checked ?? null,
          })),
        }
      : null,
  }));
}

export interface ProcessResult {
  rows: MappingRow[];
  sourceColumns: string[];
  duplicateColumns: string[];
  processingTimeMs: number;
}

/**
 * Call the Intelligent File Classification endpoint in parallel with the
 * preview upload. Returns the suggested report_type plus the full list of
 * allowed_report_types so the Preview dialog can render its File Type
 * dropdown options directly from this response.
 *
 * In mock mode we short-circuit to `unknown` with the canonical Lloyd's
 * report_types — the real cascade depends on DB-backed schema data that
 * the frontend mocks don't simulate.
 */
export async function classifyFile(
  file: File,
  schemaId: string,
  signal?: AbortSignal
): Promise<ClassificationResult> {
  if (shouldUseMocks()) {
    await new Promise((r) => setTimeout(r, 400 + Math.random() * 500));
    return {
      detectedType: "unknown",
      reason: "Mock mode — classification skipped",
      stepUsed: "none",
      score: null,
      allowedReportTypes: ["claims", "risk_premiums"],
    };
  }

  const formData = new FormData();
  formData.append("file", file);
  formData.append("schema_id", schemaId);

  const raw = await apiMultipartFetch<{
    detected_type: string;
    reason: string;
    step_used: "filename" | "none";
    score: number | null;
    allowed_report_types: string[];
  }>("/files/classify", formData, signal);

  trackEvent("Classify.Completed", {
    schemaId,
    detectedType: raw.detected_type,
    stepUsed: raw.step_used,
    fileName: file.name,
  });

  return {
    detectedType: raw.detected_type,
    reason: raw.reason,
    stepUsed: raw.step_used,
    score: raw.score,
    allowedReportTypes: raw.allowed_report_types,
  };
}

export async function processFile(
  file: File,
  fileId: string,
  schemaFields: SchemaField[],
  knownMappings: KnownMapping[],
  reportType?: string | null
): Promise<ProcessResult> {
  if (shouldUseMocks()) {
    // Parse the actual uploaded file to extract real source column headers
    const worksheets = await parseFile(file);
    const headerSet = new Set<string>();
    for (const ws of worksheets) {
      if (ws.isMetadata) continue;
      for (const h of ws.headers) headerSet.add(h);
    }
    const realSourceColumns = Array.from(headerSet);

    // Generate dynamic mappings using real headers against the schema
    const mockResult = generateDynamicMappings(realSourceColumns, schemaFields);

    trackEvent("Processing.MockCompleted", {
      fileName: file.name,
      sourceColumnCount: realSourceColumns.length,
      mappingCount: mockResult.rows.length,
      processingTimeMs: mockResult.processingTimeMs,
    });

    // Simulate realistic network + processing latency
    await new Promise((r) => setTimeout(r, 800 + Math.random() * 700));

    return mockResult;
  }

  const payload = {
    file_id: fileId,
    file_name: file.name,
    known_mappings: knownMappings.map((km) => ({
      mapping_id: km.mappingId,
      source_field: km.sourceField,
      target_field: km.targetFieldId,
      schema_id: km.schemaId,
      confidence: km.confidence ?? 1.0,
    })),
    schema_fields: schemaFields.map((f) => ({
      schema_id: f.schemaId ?? "",
      field_id: f.id,
      field_name: f.fieldName,
      field_category: f.fieldCategory,
      data_type: f.dataType,
      max_length: f.maxLength ?? null,
      definition: f.definition,
      is_mandatory: f.mandatory,
    })),
    tenant_config: {
      tenant_id: "dev-tenant-001",
      catalog_name: "klyra_bdx",
      provider: "default",
    },
    // Confirmed report_type from the Preview screen's File Type dropdown.
    // The backend uses this to filter schema_fields to the correct
    // contract (claims vs risk_premiums) before mapping runs. Null means
    // no filter — the classifier was unsure and the user hasn't been
    // prompted yet (legacy flows / non-classified schemas).
    report_type: reportType ?? null,
  };

  const formData = new FormData();
  formData.append("file", file);
  formData.append("payload", JSON.stringify(payload));

  const response = await apiMultipartFetch<ApiProcessResponse>("/process", formData);
  const { rows, sourceColumns, duplicateColumns } = transformProcessResponse(
    response,
    schemaFields
  );

  return { rows, sourceColumns, duplicateColumns, processingTimeMs: response.processing_time_ms };
}

export interface MappingProgressResult {
  fileId: string;
  totalRegions: number;
  completedRegionIds: string[];
  regions: {
    regionId: string;
    schemaId: string | null;
    reportType: string | null;
    mappingRunId: string | null;
    completedAt: string | null;
  }[];
  nextRegionId: string | null;
  allComplete: boolean;
}

export async function fetchMappingProgress(fileId: string): Promise<MappingProgressResult> {
  if (shouldUseMocks()) {
    return {
      fileId,
      totalRegions: 0,
      completedRegionIds: [],
      regions: [],
      nextRegionId: null,
      allComplete: false,
    };
  }
  const raw = await apiFetch<{
    file_id: string;
    total_regions: number;
    completed_region_ids: string[];
    regions: {
      region_id: string;
      schema_id: string | null;
      report_type: string | null;
      mapping_run_id: string | null;
      completed_at: string | null;
    }[];
    next_region_id: string | null;
    all_complete: boolean;
  }>(`/files/${encodeURIComponent(fileId)}/mapping-progress`);
  return {
    fileId: raw.file_id,
    totalRegions: raw.total_regions,
    completedRegionIds: raw.completed_region_ids,
    regions: raw.regions.map((r) => ({
      regionId: r.region_id,
      schemaId: r.schema_id,
      reportType: r.report_type,
      mappingRunId: r.mapping_run_id,
      completedAt: r.completed_at,
    })),
    nextRegionId: raw.next_region_id,
    allComplete: raw.all_complete,
  };
}

export async function processExtractionRegion(params: {
  fileId: string;
  regionId: string;
  schemaId: string;
  reportType?: string | null;
  sourceColumns: string[];
  schemaFields: SchemaField[];
  knownMappings: KnownMapping[];
}): Promise<ProcessResult> {
  if (shouldUseMocks()) {
    const mockResult = generateDynamicMappings(params.sourceColumns, params.schemaFields);
    await new Promise((r) => setTimeout(r, 400));
    return mockResult;
  }

  const payload = {
    file_id: params.fileId,
    region_id: params.regionId,
    schema_id: params.schemaId,
    report_type: params.reportType ?? null,
    source_columns: params.sourceColumns,
    known_mappings: params.knownMappings.map((km) => ({
      mapping_id: km.mappingId,
      source_field: km.sourceField,
      target_field: km.targetFieldId,
      schema_id: km.schemaId,
      confidence: km.confidence ?? 1.0,
    })),
    schema_fields: params.schemaFields.map((f) => ({
      schema_id: f.schemaId ?? "",
      field_id: f.id,
      field_name: f.fieldName,
      field_category: f.fieldCategory,
      data_type: f.dataType,
      max_length: f.maxLength ?? null,
      definition: f.definition,
      is_mandatory: f.mandatory,
    })),
    tenant_config: {
      tenant_id: "dev-tenant-001",
      catalog_name: "klyra_bdx",
      provider: "default",
    },
  };

  const response = await apiFetch<ApiProcessResponse>("/api/v2/process-extraction-region", {
    method: "POST",
    body: JSON.stringify(payload),
  });
  const { rows, sourceColumns, duplicateColumns } = transformProcessResponse(
    response,
    params.schemaFields
  );
  return { rows, sourceColumns, duplicateColumns, processingTimeMs: response.processing_time_ms };
}

export async function fetchKnownMappings(
  schemaId?: string,
  providerId?: string
): Promise<KnownMapping[]> {
  if (shouldUseMocks()) {
    return new Promise((resolve) => setTimeout(() => resolve(mockKnownMappings), 300));
  }
  const params = new URLSearchParams();
  if (schemaId) params.set("schema_id", schemaId);
  if (providerId) params.set("provider_id", providerId);
  const qs = params.toString();
  const data = await apiFetch<ApiKnownMapping[]>(`/known-mappings${qs ? `?${qs}` : ""}`);
  trackEvent("api_known_mappings_fetched", {
    count: data.length,
    schemaId: schemaId ?? "all",
    providerId: providerId ?? "all",
  });
  return transformKnownMappings(data);
}

/**
 * Fetches all rows from klyra_field_requirements via GET /api/v1/field-requirements.
 * Used by KnownMappingsPage to derive per-schema report_type dropdown options and
 * to filter mappings by (schema_id, field_id) when a report_type is selected.
 * Returns [] in mock mode — the dropdown will be empty and filtering is a no-op.
 */
export async function fetchFieldRequirements(): Promise<FieldRequirement[]> {
  if (shouldUseMocks()) {
    return [];
  }
  const data = await apiFetch<ApiFieldRequirement[]>("/field-requirements");
  trackEvent("api_field_requirements_fetched", { count: data.length });
  return transformFieldRequirements(data);
}

export async function createKnownMappings(
  mappings: {
    schemaId: string;
    sourceField: string;
    targetField: string;
    approvedBy?: string;
    /** Uploading file — used server-side to stamp provider_id (esp. for presets). */
    fileId?: string;
  }[]
): Promise<void> {
  if (mappings.length === 0) return;
  if (shouldUseMocks()) return;
  await apiFetch("/known-mappings/batch", {
    method: "POST",
    body: JSON.stringify({
      mappings: mappings.map((m) => ({
        schema_id: m.schemaId,
        source_field: m.sourceField,
        target_field: m.targetField,
        approved_by: m.approvedBy ?? "",
        ...(m.fileId ? { file_id: m.fileId } : {}),
      })),
    }),
  });
  trackEvent("api_known_mappings_created", {
    count: mappings.length,
    schemaId: mappings[0]?.schemaId ?? "unknown",
  });
}

/** Convenience wrapper for single-item callers. */
export async function createKnownMapping(mapping: {
  schemaId: string;
  sourceField: string;
  targetField: string;
  approvedBy?: string;
  fileId?: string;
}): Promise<void> {
  return createKnownMappings([mapping]);
}

export async function deleteKnownMappings(mappingIds: string[]): Promise<void> {
  if (mappingIds.length === 0) return;
  if (shouldUseMocks()) return;
  await apiFetch("/known-mappings", {
    method: "DELETE",
    body: JSON.stringify({ mapping_ids: mappingIds }),
  });
  trackEvent("api_known_mappings_deleted", { count: mappingIds.length });
}

export async function deleteKnownMapping(mappingId: string): Promise<void> {
  if (shouldUseMocks()) return;
  await apiFetch(`/known-mappings/${encodeURIComponent(mappingId)}`, {
    method: "DELETE",
  });
  trackEvent("api_known_mappings_deleted", { count: 1 });
}

export async function applyMappings(params: {
  fileId: string;
  schemaId: string;
  reportType?: string | null;
  mappings: {
    source_col: string;
    target_col: string;
    region_id?: string;
  }[];
  regionId?: string | null;
  append?: boolean;
  triggerDqx?: boolean;
  multiRegion?: boolean;
}): Promise<ApplyMappingsResult> {
  if (shouldUseMocks()) {
    return {
      rows_written: 0,
      dqx_triggered: params.triggerDqx ?? true,
      dqx_run_id: Date.now(),
      dqx_job_id: `mock-dqx-${Date.now()}`,
      dqx_trigger_message: "Mock DQX trigger accepted",
    };
  }
  const reportType = params.reportType?.trim();
  // Every caller is a pipeline (complex ingestion) flow; v1 /apply-mappings
  // remains for API compatibility but the SPA never targets it.
  const result = await apiFetch<ApplyMappingsResult>("/api/v2/apply-mappings", {
    method: "POST",
    body: JSON.stringify({
      file_id: params.fileId,
      schema_id: params.schemaId,
      ...(reportType ? { report_type: reportType } : {}),
      mappings: params.mappings,
      ...(params.regionId ? { region_id: params.regionId } : {}),
      ...(params.append !== undefined ? { append: params.append } : {}),
      ...(params.triggerDqx !== undefined ? { trigger_dqx: params.triggerDqx } : {}),
      ...(params.multiRegion ? { multi_region: true } : {}),
    }),
  });
  trackEvent("api_apply_mappings", {
    fileId: params.fileId,
    schemaId: params.schemaId,
    mappingCount: params.mappings.length,
    rowsWritten: result.rows_written,
    dqxRunId: result.dqx_run_id ?? undefined,
    dqxTriggered: result.dqx_triggered ?? undefined,
  });
  return result;
}

export async function revalidateManualCleanse(params: {
  fileId: string;
  schemaId: string;
  reportType?: string | null;
  edits: {
    cell_key: string;
    row_index: number;
    column_index: number;
    source_column_name: string;
    old_value: string;
    new_value: string;
    user_id: string;
    user_email: string;
    timestamp: string;
  }[];
}): Promise<ManualCleanseRevalidateResult> {
  if (shouldUseMocks()) {
    return {
      audit_entries_written: params.edits.length,
      rows_rewritten: params.edits.length,
      cells_applied: params.edits.length,
      dqx_triggered: true,
      dqx_run_id: Date.now(),
      dqx_job_id: `mock-dqx-${Date.now()}`,
      dqx_trigger_message: "Mock revalidate accepted",
      mapping_run_id: `mock-run-${Date.now()}`,
    };
  }
  const result = await apiFetch<ManualCleanseRevalidateResult>("/manual-cleanse/revalidate", {
    method: "POST",
    body: JSON.stringify({
      file_id: params.fileId,
      schema_id: params.schemaId,
      report_type: params.reportType ?? null,
      edits: params.edits,
    }),
  });
  trackEvent("api_manual_cleanse_revalidate", {
    fileId: params.fileId,
    editCount: params.edits.length,
    auditEntriesWritten: result.audit_entries_written,
    cellsApplied: result.cells_applied,
    dqxTriggered: result.dqx_triggered,
  });
  return result;
}

export async function fetchHistoricalFiles(providerId?: string): Promise<HistoricalFile[]> {
  if (shouldUseMocks()) {
    return new Promise((resolve) =>
      setTimeout(() => {
        const all = mockHistoricalFiles;
        resolve(providerId ? all.filter((f) => f.providerId === providerId) : all);
      }, 300)
    );
  }
  const qs = providerId ? `?provider_id=${encodeURIComponent(providerId)}` : "";
  const data = await apiFetch<ApiFile[]>(`/documents${qs}`);
  trackEvent("api_files_fetched", { count: data.length, providerId: providerId ?? "all" });
  return transformFiles(data);
}

export async function fetchColumnMappings(_fileId: string): Promise<MappingRow[]> {
  return [];
}

export async function triggerDqxValidationJob(params: {
  fileId: string;
  schemaId?: string;
  fileName?: string;
  mappingCount?: number;
}): Promise<DqxTriggerResult> {
  if (shouldUseMocks()) {
    return {
      runId: Date.now(),
      dqxJobId: `mock-dqx-${Date.now()}`,
      message: "Mock DQX trigger accepted",
    };
  }

  const rawJobId = appConfig.dqx.databricksJobId?.trim();
  if (!rawJobId) {
    throw new Error("VITE_DATABRICKS_JOB_ID is not configured.");
  }

  const jobId = Number(rawJobId);
  if (!Number.isFinite(jobId)) {
    throw new Error(`VITE_DATABRICKS_JOB_ID must be numeric. Received "${rawJobId}".`);
  }

  const body = {
    job_id: jobId,
    job_parameters: {
      file_id: params.fileId,
      schema_id: params.schemaId ?? "",
      file_name: params.fileName ?? "",
      mapping_count: String(params.mappingCount ?? 0),
    },
  };

  const data = await apiFetch<{
    run_id: number;
    dqx_job_id: string;
    message?: string | null;
  }>("/dqx/trigger", {
    method: "POST",
    body: JSON.stringify(body),
  });

  trackEvent("Dqx.TriggerAccepted", {
    runId: data.run_id,
    dqxJobId: data.dqx_job_id,
    fileId: params.fileId,
    schemaId: params.schemaId ?? "unknown",
  });

  return {
    runId: data.run_id,
    dqxJobId: data.dqx_job_id,
    message: data.message,
  };
}

export async function fetchDqxValidationJobStatus(runId: number): Promise<DqxRunStatusResult> {
  if (shouldUseMocks()) {
    return {
      runId,
      lifeCycleState: "TERMINATED",
      resultState: "SUCCESS",
    };
  }

  const data = await apiFetch<{
    run_id: number;
    life_cycle_state: string;
    result_state: string | null;
  }>(`/dqx/status/${runId}`);

  trackEvent("Dqx.StatusChecked", {
    runId: data.run_id,
    lifeCycleState: data.life_cycle_state,
    resultState: data.result_state ?? "PENDING",
  });

  return {
    runId: data.run_id,
    lifeCycleState: data.life_cycle_state,
    resultState: data.result_state ?? null,
  };
}

export async function runDqxValidation(
  worksheets: ParsedWorksheet[],
  savedMappings: KnownMapping[],
  schemaFields?: SchemaField[]
): Promise<DqxValidationResult> {
  if (shouldUseMocks()) {
    const schema = schemaFields ?? LLOYDS_52_SCHEMA;
    // Simulate processing delay
    await new Promise((r) => setTimeout(r, 600 + Math.random() * 800));

    const dqxOverride = getDqxOverride();
    const result = runMockDqxValidation(worksheets, savedMappings, schema, dqxOverride);

    trackEvent("Dqx.MockCompleted", {
      hasErrors: result.hasErrors,
      totalErrorCells: result.totalErrorCells,
      dataFormatting: result.summary["Data Formatting"],
      summationLogic: result.summary["Summation Logic"],
      structureSyntax: result.summary["Structure & Syntax"],
      dqxOverride: dqxOverride ?? "none",
    });
    return result;
  }

  // Production: POST to sidecar DQX endpoint
  // TODO: implement when sidecar DQX route is ready
  const body = {
    worksheets: worksheets.map((ws) => ({
      name: ws.name,
      headers: ws.headers,
      row_count: ws.totalRows,
    })),
    mappings: savedMappings.map((m) => ({
      source_field: m.sourceField,
      target_field: m.targetField,
    })),
  };
  const data = await apiFetch<{
    has_errors: boolean;
    total_error_cells: number;
    summary: Record<string, number>;
    cell_errors: Record<
      string,
      {
        row_index: number;
        column_index: number;
        source_column_name: string;
        target_column_name: string;
        value: string;
        rules: string[];
        category: string;
      }
    >;
    error_report: {
      column_name: string;
      column_index: number;
      row_index: number;
      value: string;
      error: string;
      rule_type?: string;
    }[];
  }>("/dqx-validate", { method: "POST", body: JSON.stringify(body) });

  // Transform API response to DqxValidationResult
  const cellErrors = new Map<string, DqxCellError>();
  for (const [key, err] of Object.entries(data.cell_errors)) {
    cellErrors.set(key, {
      rowIndex: err.row_index,
      columnIndex: err.column_index,
      sourceColumnName: err.source_column_name,
      targetColumnName: err.target_column_name,
      value: err.value,
      rules: err.rules,
      ruleNames: err.rules,
      category: err.category as DqxErrorCategory,
    });
  }

  trackEvent("Dqx.Completed", {
    hasErrors: data.has_errors,
    totalErrorCells: data.total_error_cells,
  });

  return {
    hasErrors: data.has_errors,
    totalErrorCells: data.total_error_cells,
    summary: data.summary as DqxValidationResult["summary"],
    cellErrors: cellErrors as DqxValidationResult["cellErrors"],
    errorReport: data.error_report.map((r) => ({
      columnName: r.column_name,
      columnIndex: r.column_index,
      rowIndex: r.row_index,
      value: r.value,
      error: r.error,
      ruleType: r.rule_type,
    })),
  };
}

function normalizeDqxQuarantineResponse(raw: unknown): DqxQuarantineResults {
  if (raw === null || typeof raw !== "object") {
    return { dqx_job_id: "", quarantine_table: [], quarantine_summary_table: [] };
  }
  const r = raw as Record<string, unknown>;
  const table = r.quarantine_table ?? r.quarantineTable ?? (Array.isArray(raw) ? raw : undefined);
  const summary = r.quarantine_summary_table ?? r.quarantineSummaryTable;
  const jobId = String(r.dqx_job_id ?? r.dqxJobId ?? "");
  const fileId = r.file_id ?? r.fileId;
  const schemaId = r.schema_id ?? r.schemaId;
  return {
    dqx_job_id: jobId,
    file_id: typeof fileId === "string" ? fileId : null,
    schema_id: typeof schemaId === "string" ? schemaId : null,
    quarantine_table: Array.isArray(table) ? (table as Record<string, unknown>[]) : [],
    quarantine_summary_table: Array.isArray(summary) ? (summary as Record<string, unknown>[]) : [],
  };
}

export async function fetchDqxQuarantineResults(dqxJobId: string): Promise<DqxQuarantineResults> {
  if (shouldUseMocks()) {
    return {
      dqx_job_id: dqxJobId,
      quarantine_table: [],
      quarantine_summary_table: [],
    };
  }
  const raw = await apiFetch<unknown>(`/dqx/quarantine/${encodeURIComponent(dqxJobId)}`);
  return normalizeDqxQuarantineResponse(raw);
}

export interface DqxSummaryResults {
  dqxJobId: string;
  failingRowCount: number;
  quarantineSummaryTable: Record<string, unknown>[];
}

export async function fetchDqxSummary(dqxJobId: string): Promise<DqxSummaryResults> {
  if (shouldUseMocks()) {
    return {
      dqxJobId,
      failingRowCount: 0,
      quarantineSummaryTable: [],
    };
  }

  const raw = await apiFetch<{
    dqx_job_id: string;
    failing_row_count: number;
    quarantine_summary_table: Record<string, unknown>[];
  }>(`/dqx/summary/${encodeURIComponent(dqxJobId)}`);

  return {
    dqxJobId: raw.dqx_job_id,
    failingRowCount: raw.failing_row_count,
    quarantineSummaryTable: raw.quarantine_summary_table ?? [],
  };
}

/**
 * Load explicit DQX rules extracted from the data contract YAML at the given path
 * (relative to the server-side contract base directory).
 */
export async function fetchDataContractRules(
  contractRelativePath: string
): Promise<DqxContractRulesResponse> {
  if (shouldUseMocks()) {
    return {
      contract_name: contractRelativePath,
      contract_path: "/mock/contract",
      contract_rules: [],
    };
  }
  const segments = contractRelativePath
    .split("/")
    .filter((s) => s.length > 0)
    .map((s) => encodeURIComponent(s))
    .join("/");
  return apiFetch<DqxContractRulesResponse>(`/dqx/contract-rules/${segments}`);
}

/**
 * Load DQX rules from the single `.yml`/`.yaml` under `{contract_base}/{schemaId}/` (server resolves path).
 */
export async function fetchDataContractRulesBySchema(
  schemaId: string
): Promise<DqxContractRulesResponse> {
  if (shouldUseMocks()) {
    return {
      contract_name: schemaId,
      contract_path: "/mock/contract",
      contract_rules: [],
    };
  }
  const q = new URLSearchParams({
    schema_id: schemaId,
  });
  const path = `/dqx/contract-rules/by-schema?${q.toString()}`;

  const headers = await authHeaders();
  const res = await fetch(`${appConfig.api.baseUrl}${path}`, {
    headers,
    signal: AbortSignal.timeout(appConfig.api.timeout),
  });
  const rawResponseText = await res.text();

  if (!res.ok) {
    let body: unknown;
    try {
      body = JSON.parse(rawResponseText) as unknown;
    } catch {
      body = { detail: rawResponseText || `HTTP ${res.status}` };
    }
    const msg = formatApiErrorMessage(body, res.status);
    const error = new Error(msg);
    (error as ApiResponseError).statusCode = res.status;
    (error as ApiResponseError).code = resolveApiErrorCode(body, res.status);
    trackException(error, {
      endpoint: path,
      statusCode: res.status,
      errorCode: (error as ApiResponseError).code,
      method: "GET",
    });
    throw error;
  }

  let data: DqxContractRulesResponse;
  try {
    data = JSON.parse(rawResponseText) as DqxContractRulesResponse;
  } catch (e) {
    const error = new Error(
      `contract-rules response was not valid JSON: ${e instanceof Error ? e.message : String(e)}`
    );
    (error as ApiResponseError).statusCode = res.status;
    throw error;
  }
  return data;
}

export { apiFetch };
